import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import TabsContext from "../../context/TabsContext";

/**
 * Tabs — production-ready, accessible tab component.
 *
 * Features
 * - Compound component API: <Tabs><TabList><Tab/></TabList><TabPanels><TabPanel/></TabPanels></Tabs>
 * - Controlled (value + onChange) or uncontrolled (defaultValue) usage
 * - Full keyboard support: ArrowLeft/Right (or Up/Down when vertical), Home, End
 * - Correct ARIA roles/attributes (tablist, tab, tabpanel, aria-selected, aria-controls)
 * - Disabled tabs are skipped by keyboard navigation and cannot be activated
 * - Animated sliding indicator that tracks the active tab's real position/size
 * - Horizontal or vertical orientation
 * - Lazy panel mounting (panels only render once activated, then stay mounted)
 * - Works with any number of tabs added/removed dynamically (indicator recalculates via ResizeObserver)
 *
 * No external dependencies beyond React. Styling uses Tailwind classes; swap
 * the className strings for your own design system if needed.
 */

/**
 * @param {Object} props
 * @param {string} [props.value] - Controlled active tab value.
 * @param {string} [props.defaultValue] - Initial active tab value (uncontrolled).
 * @param {(value: string) => void} [props.onChange] - Called when the active tab changes.
 * @param {'horizontal'|'vertical'} [props.orientation='horizontal']
 * @param {React.ReactNode} props.children
 * @param {string} [props.className]
 */
function Tabs({
  value,
  defaultValue,
  onChange,
  orientation = "horizontal",
  children,
  className = "",
  ...rest
}) {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue ?? null);
  const activeValue = isControlled ? value : internalValue;

  // Registry of tab metadata (value, disabled, ref) in DOM order, populated by Tab children.
  const tabsRef = useRef(new Map());
  const [, forceRender] = useState(0);

  const registerTab = useCallback((tabValue, meta) => {
    tabsRef.current.set(tabValue, meta);
    forceRender((n) => n + 1);
    return () => {
      tabsRef.current.delete(tabValue);
      forceRender((n) => n + 1);
    };
  }, []);

  const orderedTabs = useMemo(() => {
    // Order is derived from insertion order of the Map, which matches mount/render order.
    return Array.from(tabsRef.current.entries()).map(([tabValue, meta]) => ({
      value: tabValue,
      ...meta,
    }));
  }, [tabsRef.current.size, activeValue]);

  // Default to the first non-disabled tab if nothing is selected yet.
  useEffect(() => {
    if (activeValue == null && orderedTabs.length > 0) {
      const firstEnabled = orderedTabs.find((t) => !t.disabled);
      if (firstEnabled) {
        selectTab(firstEnabled.value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderedTabs.length]);

  const selectTab = useCallback(
    (newValue) => {
      if (!isControlled) setInternalValue(newValue);
      onChange?.(newValue);
    },
    [isControlled, onChange],
  );

  const focusTab = useCallback((tabValue) => {
    tabsRef.current.get(tabValue)?.ref?.current?.focus();
  }, []);

  const moveFocus = useCallback(
    (currentValue, direction) => {
      const tabs = orderedTabs;
      if (tabs.length === 0) return;
      const currentIndex = tabs.findIndex((t) => t.value === currentValue);
      let nextIndex = currentIndex;
      const step = direction === "next" ? 1 : direction === "prev" ? -1 : 0;

      if (direction === "first") {
        nextIndex = tabs.findIndex((t) => !t.disabled);
      } else if (direction === "last") {
        for (let i = tabs.length - 1; i >= 0; i--) {
          if (!tabs[i].disabled) {
            nextIndex = i;
            break;
          }
        }
      } else {
        // Walk in `step` direction, wrapping around, skipping disabled tabs.
        for (let i = 0; i < tabs.length; i++) {
          nextIndex = (nextIndex + step + tabs.length) % tabs.length;
          if (!tabs[nextIndex].disabled) break;
        }
      }

      const next = tabs[nextIndex];
      if (next) {
        selectTab(next.value);
        focusTab(next.value);
      }
    },
    [orderedTabs, selectTab, focusTab],
  );

  const contextValue = useMemo(
    () => ({
      activeValue,
      selectTab,
      orientation,
      registerTab,
      moveFocus,
    }),
    [activeValue, selectTab, orientation, registerTab, moveFocus],
  );

  return (
    <TabsContext.Provider value={contextValue}>
      <div
        className={`tabs-container${
          orientation === "vertical" ? " tab-vertical" : ""
        } ${className}`}
        data-orientation={orientation}
        {...rest}
      >
        {children}
      </div>
    </TabsContext.Provider>
  );
}

export default Tabs;

// ---------------------------------------------------------------------------
// Demo (default export) — remove this section when integrating the component
// into your own app; keep only the pieces above.
// ---------------------------------------------------------------------------

// function BarChartIcon(props) {
//   return (
//     <svg
//       width="16"
//       height="16"
//       viewBox="0 0 24 24"
//       fill="none"
//       stroke="currentColor"
//       strokeWidth="2"
//       {...props}
//     >
//       <line x1="12" y1="20" x2="12" y2="10" />
//       <line x1="18" y1="20" x2="18" y2="4" />
//       <line x1="6" y1="20" x2="6" y2="16" />
//     </svg>
//   );
// }
// function UsersIcon(props) {
//   return (
//     <svg
//       width="16"
//       height="16"
//       viewBox="0 0 24 24"
//       fill="none"
//       stroke="currentColor"
//       strokeWidth="2"
//       {...props}
//     >
//       <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
//       <circle cx="9" cy="7" r="4" />
//       <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
//       <path d="M16 3.13a4 4 0 0 1 0 7.75" />
//     </svg>
//   );
// }
// function SettingsIcon(props) {
//   return (
//     <svg
//       width="16"
//       height="16"
//       viewBox="0 0 24 24"
//       fill="none"
//       stroke="currentColor"
//       strokeWidth="2"
//       {...props}
//     >
//       <circle cx="12" cy="12" r="3" />
//       <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
//     </svg>
//   );
// }

// function TabsDemo() {
//   const [tab, setTab] = useState("overview");

//   return (
//     <div className="min-h-[420px] w-full bg-white p-8 font-sans">
//       <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(2px); } to { opacity: 1; transform: translateY(0); } }`}</style>

//       <div className="max-w-2xl mx-auto">
//         <h2 className="text-lg font-semibold text-slate-900 mb-1">
//           Account dashboard
//         </h2>
//         <p className="text-sm text-slate-500 mb-6">
//           Controlled tabs, keyboard-navigable, with a disabled panel.
//         </p>

//         <Tabs value={tab} onChange={setTab}>
//           <TabList aria-label="Dashboard sections">
//             <Tab value="overview">
//               <BarChartIcon /> Overview
//             </Tab>
//             <Tab value="team">
//               <UsersIcon /> Team
//             </Tab>
//             <Tab value="settings" disabled>
//               <SettingsIcon /> Settings
//             </Tab>
//           </TabList>

//           <TabPanels className="pt-6">
//             <TabPanel value="overview">
//               <p className="text-sm text-slate-600 leading-relaxed">
//                 Revenue is up 12% month over month. Active sessions and
//                 conversion rate both trended positively across the last
//                 reporting period.
//               </p>
//             </TabPanel>
//             <TabPanel value="team">
//               <ul className="text-sm text-slate-600 space-y-2">
//                 <li>Priya Nair — Engineering lead</li>
//                 <li>Marcus Chen — Product design</li>
//                 <li>Ava Torres — Growth</li>
//               </ul>
//             </TabPanel>
//             <TabPanel value="settings">
//               <p className="text-sm text-slate-600">
//                 Settings are disabled for this demo.
//               </p>
//             </TabPanel>
//           </TabPanels>
//         </Tabs>

//         <p className="mt-8 text-xs text-slate-400">
//           Try clicking a tab, then use ← → (or Home / End) to move focus without
//           a mouse.
//         </p>
//       </div>
//     </div>
//   );
// }
